import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
  Button,
  BrandHeaderRow,
  BrandLogo,
  Card,
  Header,
  HeaderIconButton,
  HeaderText,
  HomeIcon,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';

const FaqCard = styled(Card)`
  display: grid;
  gap: 1.4rem;
`;

const FaqSection = styled.section`
  display: grid;
  gap: 0.85rem;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  min-height: 56px;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.09);
    outline: none;
  }
`;

const SectionTitle = styled.span`
  margin: 0;
  color: #93c5fd;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const SectionIcon = styled.span`
  position: relative;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  color: #fbbf24;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 18px;
    height: 3px;
    border-radius: 999px;
    background: currentColor;
  }

  &::after {
    transform: ${({ $open }) => ($open ? 'rotate(0deg)' : 'rotate(90deg)')};
  }
`;

const SectionItems = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const FaqItem = styled.article`
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(251, 191, 36, 0.07)),
    rgba(255, 255, 255, 0.045);
`;

const Question = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: clamp(1.05rem, 3vw, 1.3rem);
  font-weight: 950;
  line-height: 1.15;
`;

const Answer = styled.p`
  margin: 0.45rem 0 0;
  color: rgba(226, 232, 240, 0.74);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.45;
`;

const PreviewButton = styled(Button)`
  margin-top: 0.9rem;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(12px);
`;

const Modal = styled.div`
  width: min(560px, 100%);
  max-height: min(760px, calc(100dvh - 2rem));
  overflow: auto;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.85rem;
  color: #f8fafc;
  font-size: 1.35rem;
  font-weight: 950;
  line-height: 1.15;
  text-align: center;
`;

const ModalActions = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 0.85rem;
  padding: 0.75rem 0 0;
  background: linear-gradient(to bottom, rgba(15, 23, 42, 0.72), #0f172a 28%);
`;

const EmailPreviewShell = styled.div`
  display: grid;
  gap: 0.8rem;
`;

const EmailPreviewMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.62);
  color: #cbd5e1;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;

  strong {
    color: #f8fafc;
  }
`;

const EmailPreviewCard = styled.div`
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  color: #111827;
`;

const EmailPreviewHeader = styled.div`
  padding: 1.35rem 1.2rem 0.8rem;
`;

const EmailPreviewBrand = styled.div`
  color: #9a6700;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const EmailPreviewHeadline = styled.h3`
  margin: 0.55rem 0 0;
  color: #111827;
  font-size: clamp(1.45rem, 5vw, 1.9rem);
  font-weight: 950;
  line-height: 1.16;
`;

const EmailPreviewBody = styled.div`
  display: grid;
  gap: 1rem;
  padding: 0 1.2rem 1.35rem;
  color: #4b5563;
  font-size: 0.98rem;
  line-height: 1.55;

  p {
    margin: 0;
  }
`;

const EmailPreviewBlock = styled.div`
  display: grid;
  gap: 0.35rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const EmailPreviewLabel = styled.div`
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const EmailPreviewPrimary = styled.div`
  color: #111827;
  font-size: 1.12rem;
  font-weight: 900;
  line-height: 1.45;
`;

const EmailPreviewMessage = styled.div`
  color: #111827;
  font-size: clamp(1.25rem, 4.5vw, 1.55rem);
  font-weight: 950;
  line-height: 1.35;
  white-space: pre-wrap;
`;

const EmailPreviewFooter = styled.div`
  padding: 0.85rem 1.2rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.45;
`;

const faqSections = [
  {
    title: 'Emails and Consequences',
    items: [
      {
        question: 'Does the email come from me?',
        answer: 'It comes from a PlacePledge email address with your name shown as the sender, like "Sam via PlacePledge." The email is sent to your chosen contact with you copied, so both of you see the same message.',
        sampleEmailPreview: true
      },
      {
        question: 'What happens if I miss a pledge?',
        answer: 'PlacePledge sends the consequence email you wrote to the accountability contact you chose. The point is to make skipping feel real before you decide to skip.'
      },
      {
        question: 'Can I choose who receives the email?',
        answer: 'Yes. You choose the accountability contact when you create the pledge.'
      },
      {
        question: 'Will my friend have to sign up?',
        answer: 'No. They can receive the accountability email without creating a PlacePledge account.'
      },
      {
        question: 'Will PlacePledge send the email automatically?',
        answer: 'Yes. If the pledge is missed, PlacePledge sends the prewritten consequence email automatically.'
      },
      {
        question: 'Can I preview the email before it sends?',
        answer: 'Yes. The create pledge screen includes a preview so you can see the message before sealing the pledge.'
      },
      {
        question: 'Can I use a custom message?',
        answer: 'Yes. You write the message yourself, from simple accountability to something more pointed.'
      },
      {
        question: 'Can I send the email to myself?',
        answer: 'Yes. You can use your own email if self-accountability is what you want.'
      }
    ]
  },
  {
    title: 'Locations and Privacy',
    items: [
      {
        question: 'How do I complete a pledge?',
        answer: 'Bring your phone to the location before the deadline. PlacePledge uses your phone location to confirm that you showed up.'
      },
      {
        question: 'Can I choose the location myself?',
        answer: 'Yes. You can search for an address or tap the map to drop a pin, then use that place for your pledge.'
      },
      {
        question: 'How close do I need to be to the location?',
        answer: 'The app uses the location radius set for your pledge destination. You can set the minimum radius in Settings, then you just need to get within that accepted range before the deadline.'
      },
      {
        question: 'What if GPS is inaccurate?',
        answer: 'GPS can be imperfect, especially indoors or around tall buildings. Pick a location and radius that gives you a little practical margin.'
      },
      {
        question: 'Does PlacePledge track my location all the time?',
        answer: 'No. PlacePledge only asks for your current location when you press the fulfillment button to check whether you are at the pledged location.'
      },
      {
        question: 'How do I know PlacePledge is not tracking me all day?',
        answer: 'PlacePledge is a web app, so location access runs through your browser permissions. It is designed to check your location only when you press the fulfillment button, not to collect a route or background location history. You can also revoke location permission in your browser at any time.'
      },
      {
        question: 'What location data is stored?',
        answer: 'PlacePledge stores the pledged destination because it needs that location to check your pledge. Separately, you can choose to save that destination as a reusable location for future pledges. The current location checked when you press the fulfillment button is used only for that check and is not stored.'
      }
    ]
  },
  {
    title: 'Managing Pledges',
    items: [
      {
        question: 'Can I cancel or edit a pledge after creating it?',
        answer: 'Pledges can only be edited or deleted for a short window after creation. After that, they lock so the consequence stays meaningful.'
      },
      {
        question: 'Can I make multiple pledges at once?',
        answer: 'Yes. You can create multiple pledges for different places, dates, and consequences.'
      },
      {
        question: 'What happens if I arrive late?',
        answer: 'If you miss the deadline, the pledge is treated as missed and the consequence can be triggered.'
      },
      {
        question: 'Can I use this for waking up?',
        answer: 'Yes. Pick a place just outside your house, set the deadline, and move your phone there to prove you got up.'
      },
      {
        question: 'Is there a way to prove I showed up without sending anything?',
        answer: 'Yes. Fulfill the pledge by getting your phone to the location before the deadline and no consequence email is sent.'
      }
    ]
  },
  {
    title: 'Demo, Account, and Edge Cases',
    items: [
      {
        question: 'Are demo emails actually sent?',
        answer: 'Demo pledges use sample contacts and previews so you can understand the flow without surprising anyone.'
      },
      {
        question: 'What happens if my phone dies?',
        answer: 'If your phone cannot prove you made it, the app may not be able to mark the pledge fulfilled. Keep it charged for pledges that matter.'
      },
      {
        question: 'What if location permissions are turned off?',
        answer: 'PlacePledge needs location permission to verify a pledge. If permissions are off, it cannot reliably confirm that you showed up.'
      },
      {
        question: 'What if I lose internet connection?',
        answer: 'You need a working connection for the app to sync pledge status. For important pledges, open the app while connected before the deadline.'
      },
      {
        question: 'Can I delete my account and data?',
        answer: 'Yes. Account deletion is available from Settings.'
      }
    ]
  }
];

const sampleEmail = {
  to: 'jordan@example.com',
  cc: 'maya@example.com',
  subject: 'Maya missed a PlacePledge',
  senderName: 'Maya',
  locationName: 'Downtown Gym',
  dueAt: 'Tomorrow at 7:00 AM',
  message: 'IOU $20 for not showing up'
};

const Faq = () => {
  const { user } = useAuth();
  const homePath = user ? '/dashboard' : '/login';
  const [isSampleEmailOpen, setIsSampleEmailOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => new Set([faqSections[0].title]));

  const toggleSection = (sectionTitle) => {
    setOpenSections((currentSections) => {
      const nextSections = new Set(currentSections);

      if (nextSections.has(sectionTitle)) {
        nextSections.delete(sectionTitle);
      } else {
        nextSections.add(sectionTitle);
      }

      return nextSections;
    });
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderIconButton as={Link} to={homePath} aria-label="Home">
                <HomeIcon aria-hidden="true" />
              </HeaderIconButton>
            </BrandHeaderRow>
            <Title>FAQ.</Title>
            <Subtitle>Quick answers about pledges, locations, and consequence emails.</Subtitle>
          </HeaderText>
        </Header>

        <FaqCard>
          {faqSections.map((section) => (
            <FaqSection key={section.title}>
              <SectionHeader
                type="button"
                aria-expanded={openSections.has(section.title)}
                aria-controls={`faq-section-${section.title.replace(/\W+/g, '-').toLowerCase()}`}
                onClick={() => toggleSection(section.title)}
              >
                <SectionTitle>{section.title}</SectionTitle>
                <SectionIcon $open={openSections.has(section.title)} aria-hidden="true" />
              </SectionHeader>
              {openSections.has(section.title) && (
                <SectionItems id={`faq-section-${section.title.replace(/\W+/g, '-').toLowerCase()}`}>
                  {section.items.map((item) => (
                    <FaqItem key={item.question}>
                      <Question>{item.question}</Question>
                      <Answer>{item.answer}</Answer>
                      {item.sampleEmailPreview && (
                        <PreviewButton type="button" onClick={() => setIsSampleEmailOpen(true)}>
                          Preview a sample email
                        </PreviewButton>
                      )}
                    </FaqItem>
                  ))}
                </SectionItems>
              )}
            </FaqSection>
          ))}
        </FaqCard>

        {isSampleEmailOpen && (
          <ModalBackdrop onMouseDown={() => setIsSampleEmailOpen(false)}>
            <Modal
              role="dialog"
              aria-modal="true"
              aria-labelledby="sample-email-preview-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <ModalTitle id="sample-email-preview-title">Email preview</ModalTitle>
              <EmailPreviewShell>
                <EmailPreviewMeta>
                  <div><strong>To:</strong> {sampleEmail.to}</div>
                  <div><strong>Cc:</strong> {sampleEmail.cc}</div>
                  <div><strong>Subject:</strong> {sampleEmail.subject}</div>
                </EmailPreviewMeta>
                <EmailPreviewCard>
                  <EmailPreviewHeader>
                    <EmailPreviewBrand>PlacePledge</EmailPreviewBrand>
                    <EmailPreviewHeadline>{sampleEmail.senderName} missed a PlacePledge</EmailPreviewHeadline>
                  </EmailPreviewHeader>
                  <EmailPreviewBody>
                    <p>They precommitted to send this accountability note if they missed their pledge.</p>
                    <EmailPreviewBlock>
                      <EmailPreviewLabel>The pledge</EmailPreviewLabel>
                      <EmailPreviewPrimary>Be at {sampleEmail.locationName}</EmailPreviewPrimary>
                      <p>by {sampleEmail.dueAt}</p>
                    </EmailPreviewBlock>
                    <EmailPreviewBlock>
                      <EmailPreviewLabel>Their message</EmailPreviewLabel>
                      <EmailPreviewMessage>{sampleEmail.message}</EmailPreviewMessage>
                    </EmailPreviewBlock>
                    <p>PlacePledge helps people make location-based promises with real consequences.</p>
                  </EmailPreviewBody>
                  <EmailPreviewFooter>
                    This is a sample preview. No email is sent from this FAQ.
                  </EmailPreviewFooter>
                </EmailPreviewCard>
              </EmailPreviewShell>
              <ModalActions>
                <Button type="button" onClick={() => setIsSampleEmailOpen(false)}>
                  OK
                </Button>
              </ModalActions>
            </Modal>
          </ModalBackdrop>
        )}
      </Shell>
    </Page>
  );
};

export default Faq;
